import {
  Chart as ChartJS,
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  TimeScale,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import { enUS, fr, es, sv } from "date-fns/locale";
import {
  formattedDate,
  localeMap,
} from "../../components/formatted/FormattedDateAndTime";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Cookie from "js-cookie";
import { useTranslation } from "react-i18next";
import { getChartTooltipOptions } from "../../ui/components/tooltipTheme";
import { useAppZoom } from "../../contexts/AppZoomContext";
import { getEchartsZoomCompensation } from "./echartsZoom";

const formatTooltipValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return value ?? "";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

ChartJS.register(
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  TimeScale,
  Tooltip,
);

const wasteCategoryColor = (category, waste) => {
  const res = category.find((item) => item.label === waste.label);
  if (res) {
    return res?.displayColor;
  }
  return waste.backgroundColor;
};

const hasTimeCoordinates = (points) =>
  Array.isArray(points) &&
  points.some((point) => {
    if (!point || typeof point !== "object" || point.x == null) return false;
    if (typeof point.x === "number") return true;
    return !Number.isNaN(Date.parse(point.x));
  });

const sortTimeSeries = (points) => {
  if (!hasTimeCoordinates(points)) return points;
  return [...points].sort((a, b) => new Date(a.x) - new Date(b.x));
};

const getDateFnsLocale = (language) => {
  const languageCode = (language || "en").split("-")[0];
  const localeKey = localeMap[languageCode] || localeMap.en || "en-US";
  switch (localeKey) {
    case "fr-FR":
      return fr;
    case "es-ES":
      return es;
    case "sv-SE":
      return sv;
    case "en-US":
    default:
      return enUS;
  }
};

const MultipleLineAndBarChart = ({
  stackedChart,
  type,
  labelsChart,
  borderWidthChart,
  aspectRatioChart,
  datasetsChart,
  indexAxisChart,
  maintainAspectRatioChart,
  displayGrid,
  displayTicks,
  xAxisType,
  reverseChart,
  displayLegend,
  displayTooltip,
  messageLabelTooltip,
  unitValueChart,
  maxYAxis,
  additionalInfo,
  timeUnitChart = "month",
  xAxisTickCallback,
  barPercentageChart = 0.9,
  categoryPercentageChart = 1,
  maxBarThicknessChart,
  xAxisOffset,
  timeRounded = true,
}) => {
  const { t, i18n } = useTranslation();
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookie.get("userToken");
  const [chartData, setChartData] = useState(null);
  const wrapperRef = useRef(null);
  const chartRef = useRef(null);
  const { zoomLevel } = useAppZoom();
  const { compensationStyle, isDisplayZoomed } =
    getEchartsZoomCompensation(zoomLevel);

  useEffect(() => {
    setChartData(null);
    axios
      .get(`${API}wastecategory?page=1&limit=100`, {
        headers: { authorization: `Bearer ${token}` },
        params: {
          keywordFilter: "",
          listUnCodeFullFilter: {},
          listUnCodeEmptyFilter: {},
          listWasteCodeFullFilter: {},
          listWasteCodeEmptyFilter: {},
          sortByField: "label",
          orderSortByField: "1",
        },
      })
      .then((response) => {
        if (type === "bar" || type === "line") {
          const newData = datasetsChart.map((item) => {
            const color = wasteCategoryColor(response.data.data, item);
            return {
              ...item,
              data: sortTimeSeries(item.data),
              backgroundColor: color,
              ...(type === "line"
                ? {
                    borderColor: color,
                  }
                : {}),
              ...(type === "bar" && !displayTooltip
                ? {
                    hoverBackgroundColor: color,
                    hoverBorderColor: color,
                  }
                : {}),
              ...(type === "bar" && maxBarThicknessChart != null
                ? { maxBarThickness: maxBarThicknessChart }
                : {}),
            };
          });
          setChartData(newData);
        } else {
          setChartData(datasetsChart);
        }
      });
  }, [datasetsChart, API, token, type, maxBarThicknessChart, displayTooltip]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    // Without explicit sizes Chart.js measures getBoundingClientRect(),
    // which includes the zoom-compensation scale() and shrinks the canvas.
    const resize = () => {
      const chart = chartRef.current;
      const container = chart?.canvas?.parentNode;
      if (!chart?.resize || !container) return;
      chart.resize(container.clientWidth, container.clientHeight);
    };

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    observer?.observe(wrapper);
    window.addEventListener("resize", resize);
    resize();

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [zoomLevel, chartData]);

  const optionsMultipleChart = {
    responsive: true,
    maintainAspectRatio: maintainAspectRatioChart,
    aspectRatio: aspectRatioChart,
    indexAxis: indexAxisChart,
    borderSkipped: false,
    borderWidth: borderWidthChart,
    barPercentage: barPercentageChart,
    categoryPercentage: categoryPercentageChart,
    scales: {
      x: {
        stacked: stackedChart,
        beginAtZero: true,
        offset: xAxisOffset ?? type === "bar",
        offsetAfterAutoskip: true,

        grid: {
          display: displayGrid,
          drawTicks: displayGrid,
        },
        border: {
          display: false,
        },
        ticks: {
          precision: 0,
          display: displayTicks,
          maxRotation: 0,
          autoSkip: true,
          ...(xAxisType === "time" ? { source: "data" } : {}),
          ...(typeof xAxisTickCallback === "function"
            ? { callback: xAxisTickCallback }
            : {}),
        },
        type: xAxisType === "time" ? "time" : xAxisType || "category",
        ...(xAxisType === "time"
          ? {
              time: {
                isoWeekday: true,
                rounded: timeRounded,
                unit: timeUnitChart,
                displayFormats: {
                  month: "MMM yyyy",
                  week: "II",
                  day: "d MMM",
                  quarter: "QQQ yyyy",
                },
              },
              adapters: {
                date: {
                  locale: getDateFnsLocale(i18n.language),
                },
              },
            }
          : {}),
      },
      y: {
        stacked: stackedChart,
        beginAtZero: true,
        max: maxYAxis,
        reverse: reverseChart,
        grid: {
          display: displayGrid,
          drawTicks: displayGrid,
          drawOnChartArea: displayGrid,
        },
        border: {
          display: false,
        },
        ticks: {
          precision: 0,
          display: displayTicks,
          callback: function (value, index, values) {
            return value + " " + unitValueChart;
          },
        },
      },
    },
    interaction: {
      mode: "nearest",
      intersect: true,
      axis: "x",
    },
    ...(displayTooltip
      ? {}
      : {
          events: [],
        }),
    plugins: {
      legend: {
        display: displayLegend,
        position: "bottom",
        align: "start",
        labels: {
          boxWidth: 20,
          boxHeight: 20,
          padding: 15,
          textAlign: "left",
          usePointStyle: true,
          useBorderRadius: true,
          borderRadius: 30,
          font: {
            size: 10,
          },
        },
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        enabled: displayTooltip,
        ...getChartTooltipOptions(),
        callbacks: {
          title: function (context) {
            const point = context[0]?.dataset?.data?.[context[0].dataIndex];
            const label = point?.x ?? context[0]?.label ?? "";
            return xAxisType === "time"
              ? `${formattedDate(label, t)}`
              : String(label);
          },
          label: function (context) {
            const point = context.dataset.data[context.dataIndex];
            const isPointObject = point && typeof point === "object";
            const value = isPointObject ? point.y : point;
            const type =
              (isPointObject && point.z) || context.dataset.label || "";
            const count = isPointObject ? point.count || "" : "";

            let message = `${type}: ${formatTooltipValue(value)}${unitValueChart} ${messageLabelTooltip}`;
            if (additionalInfo) {
              let materialCountMessage = ` (${count} ${count > 1 ? "objets" : "objet"})`;
              message = message + materialCountMessage;
            }
            return message;
          },
        },
      },
    },
  };

  return chartData ? (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          ...(isDisplayZoomed
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : null),
        }}
      >
        <div
          style={
            isDisplayZoomed
              ? { ...compensationStyle, position: "relative" }
              : { position: "absolute", inset: 0 }
          }
        >
          {type === "bar" ? (
            <Bar
              ref={chartRef}
              data={{
                labels: labelsChart,
                datasets: chartData,
              }}
              options={optionsMultipleChart}
              style={{ width: "100%", height: "100%" }}
            />
          ) : (
            <Line
              ref={chartRef}
              data={{
                labels: labelsChart,
                datasets: chartData,
              }}
              options={optionsMultipleChart}
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </div>
      </div>
    </div>
  ) : (
    ""
  );
};

export default MultipleLineAndBarChart;
